import { useMemo, useRef, useState, type FormEvent } from 'react';
import { ExternalLink } from 'lucide-react';
import type { TurnstileInstance } from '@marsidev/react-turnstile';
import {
  US_STATES, CA_PROVINCES, CA_POSTAL_LETTER_TO_PROVINCE,
  INCENTIVE_EQUIPMENTS, INCENTIVE_EQUIPMENT_LABELS,
  type IncentiveCountry, type IncentiveEquipment, type IncentiveResults, type IncentiveProgram,
} from '@evi/shared';
import { ShopTurnstile, hasTurnstile } from '@/components/ShopTurnstile';
import { fetchIncentives, submitLead } from '@/lib/api';
import styles from './IncentivesPage.module.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * /incentives — EV-charging incentive finder. Pick a country, then a state /
 * province (autocomplete, optional zip / postal), then one of four equipment
 * classes, then search. US results proxy DSIRE server-side (may come back
 * "coming soon" until the key is wired); Canada results are a curated dataset.
 * Federal programs render first. Every card links out to the administrator.
 */

/** "Verified <Month Year>" from an ISO date, or null. Curated (Canada) cards
 *  carry this; DSIRE (US) cards do not. */
function verifiedLabel(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `Verified ${d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
}

export function IncentivesPage() {
  const [country, setCountry] = useState<IncentiveCountry | null>(null);
  const [regionInput, setRegionInput] = useState('');
  const [zip, setZip] = useState('');
  const [equipment, setEquipment] = useState<IncentiveEquipment>('all');
  const [results, setResults] = useState<IncentiveResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const regionList = country === 'CA' ? CA_PROVINCES : US_STATES;
  const regionNoun = country === 'CA' ? 'province' : 'state';

  // Resolve the typed value to a region code: exact code or exact name,
  // case-insensitive. Empty when the input is not yet a valid region.
  const resolvedCode = useMemo(() => {
    const q = regionInput.trim().toLowerCase();
    if (!q) return '';
    const byCode = regionList.find((r) => r.code.toLowerCase() === q);
    if (byCode) return byCode.code;
    const byName = regionList.find((r) => r.name.toLowerCase() === q);
    return byName ? byName.code : '';
  }, [regionInput, regionList]);

  const chooseCountry = (c: IncentiveCountry) => {
    setCountry(c);
    setRegionInput('');
    setZip('');
    setResults(null);
    setError(null);
  };

  // Canada postal helper: the first letter pre-selects the province. The user
  // can still change it. US zip is passed through untouched.
  const onZipChange = (v: string) => {
    setZip(v);
    if (country !== 'CA') return;
    const code = CA_POSTAL_LETTER_TO_PROVINCE[v.trim().charAt(0).toUpperCase()];
    const prov = code ? CA_PROVINCES.find((p) => p.code === code) : undefined;
    if (prov) setRegionInput(prov.name);
  };

  const canSearch = country !== null && resolvedCode !== '' && !loading;

  const search = async () => {
    if (!country || !resolvedCode || loading) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchIncentives({
        country,
        region: resolvedCode,
        equipment,
        ...(zip.trim() ? { zip: zip.trim() } : {}),
      });
      setResults(data);
    } catch {
      setError('Could not load incentives just now. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      {/* Page-top hero banner (media/incentives/hero.webp, converted per
          the shop image conventions). width/height pin the intrinsic
          ratio so the banner never causes layout shift. */}
      <img
        src="/media/incentives/hero.webp"
        alt="EV charging incentives"
        className={styles.hero}
        width={1376}
        height={768}
      />
      <header className={styles.head}>
        <p className={styles.eyebrow}>Incentives</p>
        <h1 className={styles.title}>Find EV charging incentives in your area</h1>
        <p className={styles.intro}>
          Rebates and grants for charging equipment change often and vary by where you are. Pick
          your country and region, choose the equipment you are planning, and we will point you to
          the programs worth a look.
        </p>
      </header>

      <fieldset className={styles.step}>
        <legend className={styles.legend}>1. Country</legend>
        <div className={styles.pills}>
          {(['US', 'CA'] as const).map((c) => (
            <button
              key={c}
              type="button"
              className={`${styles.pill} ${country === c ? styles.pillActive : ''}`}
              onClick={() => chooseCountry(c)}
            >
              {c === 'US' ? '\u{1F1FA}\u{1F1F8} United States' : '\u{1F1E8}\u{1F1E6} Canada'}
            </button>
          ))}
        </div>
      </fieldset>

      {country && (
        <>
          <fieldset className={styles.step}>
            <legend className={styles.legend}>2. {regionNoun === 'province' ? 'Province' : 'State'}</legend>
            <div className={styles.regionRow}>
              <input
                className={styles.input}
                list="incentiveRegions"
                value={regionInput}
                onChange={(e) => setRegionInput(e.target.value)}
                placeholder={`Start typing your ${regionNoun}`}
                aria-label={regionNoun}
              />
              <datalist id="incentiveRegions">
                {regionList.map((r) => <option key={r.code} value={r.name} />)}
              </datalist>
              <input
                className={styles.inputSmall}
                value={zip}
                onChange={(e) => onZipChange(e.target.value)}
                placeholder={country === 'CA' ? 'Postal (optional)' : 'ZIP (optional)'}
                maxLength={country === 'CA' ? 7 : 5}
                aria-label={country === 'CA' ? 'Postal code' : 'ZIP code'}
              />
            </div>
            {country === 'CA' && (
              <p className={styles.hint}>Tip: enter your postal code and we will pre-select the province.</p>
            )}
          </fieldset>

          <fieldset className={styles.step}>
            <legend className={styles.legend}>3. Equipment</legend>
            <div className={styles.pills}>
              {INCENTIVE_EQUIPMENTS.map((eq) => (
                <button
                  key={eq}
                  type="button"
                  className={`${styles.pill} ${equipment === eq ? styles.pillActive : ''}`}
                  onClick={() => setEquipment(eq)}
                >
                  {INCENTIVE_EQUIPMENT_LABELS[eq]}
                </button>
              ))}
            </div>
          </fieldset>

          <button
            type="button"
            className="eviBtn eviBtnPrimary"
            disabled={!canSearch}
            onClick={search}
          >
            {loading ? 'Searching…' : 'Find incentives'}
          </button>
          {regionInput.trim() !== '' && resolvedCode === '' && (
            <p className={styles.hint}>Pick a {regionNoun} from the list to search.</p>
          )}
        </>
      )}

      {error && <p className={styles.error}>{error}</p>}

      {results && !loading && (
        <section className={styles.results}>
          {results.unavailable ? (
            <div className={styles.unavailable}>{results.unavailable.reason}</div>
          ) : results.programs.length === 0 ? (
            <p className={styles.empty}>
              No programs found for that selection right now. Programs change often, so check back or
              confirm directly with your utility or agency.
            </p>
          ) : (
            <ul className={styles.cards}>
              {results.programs.map((p) => <IncentiveCard key={p.id} program={p} />)}
            </ul>
          )}
        </section>
      )}

      <SubscribeForm country={country} region={resolvedCode} />

      <footer className={styles.footerNote}>
        Programs change. Confirm details with the administrator before purchase.
      </footer>
    </div>
  );
}

/**
 * "Get notified when new programs open in your area" capture. Posts a lead
 * (source incentive_alerts, tags carry the country + region) with Turnstile
 * fail-closed. Prefills from the page's current country/region pick. The alert
 * sends themselves are a later cadence; this only captures interest.
 */
function SubscribeForm({ country, region }: { country: IncentiveCountry | null; region: string }) {
  const [email, setEmail] = useState('');
  const [c, setC] = useState<IncentiveCountry>(country ?? 'US');
  const [regionInput, setRegionInput] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const tsRef = useRef<TurnstileInstance>(null);

  // Prefill the region field from the page's resolved pick (once, non-blocking).
  const prefill = useMemo(() => {
    if (!region) return '';
    const list = (country ?? c) === 'CA' ? CA_PROVINCES : US_STATES;
    return list.find((r) => r.code === region)?.name ?? '';
  }, [region, country, c]);
  const effectiveRegionInput = regionInput || prefill;

  const regionList = c === 'CA' ? CA_PROVINCES : US_STATES;
  const resolvedRegion = useMemo(() => {
    const q = effectiveRegionInput.trim().toLowerCase();
    if (!q) return '';
    return regionList.find((r) => r.code.toLowerCase() === q || r.name.toLowerCase() === q)?.code ?? '';
  }, [effectiveRegionInput, regionList]);

  const canSubmit = hasTurnstile && !!token && EMAIL_RE.test(email.trim()) && status !== 'sending';

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !token) return;
    setStatus('sending');
    const tags = ['country:' + c, ...(resolvedRegion ? ['region:' + resolvedRegion] : [])];
    try {
      await submitLead({ email: email.trim(), source: 'incentive_alerts', tags, turnstile_token: token });
      setStatus('done');
    } catch {
      setStatus('error');
      setToken(null);
      tsRef.current?.reset();
    }
  };

  if (status === 'done') {
    return (
      <section className={styles.subscribe}>
        <p className={styles.subscribeDone}>
          You&rsquo;re on the list. We&rsquo;ll let you know when new programs open in your area.
        </p>
      </section>
    );
  }

  return (
    <section className={styles.subscribe}>
      <h2 className={styles.subscribeTitle}>Get notified when new programs open in your area</h2>
      <p className={styles.subscribeSub}>
        Incentives change often. Leave your email and where you are, and we will reach out when new
        programs open near you.
      </p>
      <form className={styles.subscribeForm} onSubmit={onSubmit}>
        <div className={styles.subscribeRow}>
          <input
            type="email"
            className={`${styles.input} clarity-mask`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            aria-label="Email"
            required
          />
          <select
            className={styles.inputSmall}
            value={c}
            onChange={(e) => { setC(e.target.value as IncentiveCountry); setRegionInput(''); }}
            aria-label="Country"
          >
            <option value="US">{'\u{1F1FA}\u{1F1F8}'} United States</option>
            <option value="CA">{'\u{1F1E8}\u{1F1E6}'} Canada</option>
          </select>
          <input
            className={styles.inputSmall}
            list="subscribeRegions"
            value={effectiveRegionInput}
            onChange={(e) => setRegionInput(e.target.value)}
            placeholder={c === 'CA' ? 'Province' : 'State'}
            aria-label={c === 'CA' ? 'Province' : 'State'}
          />
          <datalist id="subscribeRegions">
            {regionList.map((r) => <option key={r.code} value={r.name} />)}
          </datalist>
        </div>
        <ShopTurnstile ref={tsRef} onToken={setToken} />
        {!hasTurnstile && (
          <p className={styles.hint}>Verification is unavailable right now. Please try again later.</p>
        )}
        {status === 'error' && <p className={styles.error}>Could not save your email. Please try again.</p>}
        <button type="submit" className="eviBtn eviBtnPrimary" disabled={!canSubmit}>
          {status === 'sending' ? 'Saving…' : 'Notify me'}
        </button>
      </form>
    </section>
  );
}

function IncentiveCard({ program }: { program: IncentiveProgram }) {
  const verified = verifiedLabel(program.verified_at);
  return (
    <li className={program.is_federal ? `${styles.card} ${styles.cardFederal}` : styles.card}>
      <a href={program.url} target="_blank" rel="noreferrer" className={styles.cardLink}>
        <div className={styles.cardHead}>
          {program.is_federal && <span className={styles.federalTag}>Federal</span>}
          <span className={styles.cardJurisdiction}>{program.jurisdiction}</span>
          <ExternalLink size={14} className={styles.cardExt} aria-hidden />
        </div>
        <h3 className={styles.cardName}>{program.name}</h3>
        <p className={styles.cardAdmin}>{program.administrator}</p>
        <p className={styles.cardSummary}>{program.summary}</p>
        {program.eligibility_notes && (
          <p className={styles.cardEligibility}>{program.eligibility_notes}</p>
        )}
        <div className={styles.cardMeta}>
          {program.amount && <span className={styles.cardAmount}>{program.amount}</span>}
          {program.status && <span className={styles.cardStatus}>{program.status}</span>}
          {program.deadline && <span className={styles.cardStatus}>Deadline: {program.deadline}</span>}
          {verified && <span className={styles.cardVerified}>{verified}</span>}
        </div>
      </a>
    </li>
  );
}
